'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, TrendingUp, BarChart2, BookOpen, Zap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const features = [
  { id: 'feat-scan', icon: Zap, text: 'Auto-scan for fresh Order Blocks' },
  { id: 'feat-mtf', icon: TrendingUp, text: 'Multi-timeframe direction alignment' },
  { id: 'feat-signal', icon: BarChart2, text: 'Structured signal approval workflow' },
  { id: 'feat-journal', icon: BookOpen, text: 'Trade journal with lesson tracking' },
];

export default function AuthScreen() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const { signIn, signUp, user, loading: authLoading } = useAuth();
  const redirectStarted = useRef(false);

  const navigateToPostLogin = useCallback(() => {
    window.location.href = '/api/auth/post-login';
  }, []);

  // Already authenticated (returning visit) → redirect (middleware also covers this)
  useEffect(() => {
    if (!authLoading && user && !redirectStarted.current) {
      redirectStarted.current = true;
      navigateToPostLogin();
    }
  }, [authLoading, user, navigateToPostLogin]);

  const validate = (): string | null => {
    if (!email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email address.';
    if (!password) return 'Password is required.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    if (mode === 'signup' && !fullName.trim()) return 'Full name is required.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    // Block the "already authenticated" effect so it never shows a redirect page.
    // The button spinner (isLoading) stays visible until navigation happens.
    redirectStarted.current = true;
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email.trim(), password);
      } else {
        await signUp(email.trim(), password, fullName.trim());
      }
      navigateToPostLogin();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Authentication failed. Please try again.';
      setErrorMsg(message);
      setIsLoading(false);
      redirectStarted.current = false;
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setErrorMsg('');
    setSuccessMsg('');
    setPassword('');
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] bg-primary px-12 py-10">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-bold text-[18px] text-white tracking-tight">TradeControl</span>
        </div>
        <div>
          <h1 className="text-[36px] font-extrabold text-white leading-tight mb-4">
            Your personal<br />trading control tower
          </h1>
          <p className="text-[15px] text-white/70 leading-relaxed mb-8">
            Convert your SMC trading style into a disciplined, repeatable signal workflow — from
            Order Block detection to performance analytics.
          </p>
          <div className="space-y-3">
            {features.map((f) => {
              const FeatureIcon = f.icon;
              return (
                <div key={f.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <FeatureIcon size={15} className="text-white" />
                  </div>
                  <span className="text-[13px] text-white/80 font-medium">{f.text}</span>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-[12px] text-white/40">© 2026 TradeControl. All rights reserved.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-[16px] text-foreground">TradeControl</span>
          </div>

          <h2 className="text-[24px] font-bold text-foreground mb-1">
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="text-[13px] text-muted-foreground mb-7">
            {mode === 'login' ?'Enter your credentials to access your dashboard' :'Start building your trading decision system'}
          </p>

          {successMsg && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/30 text-[13px] text-green-600 dark:text-green-400">
              {successMsg}
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-[13px] text-red-600 dark:text-red-400">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {mode === 'signup' && (
              <div>
                <label htmlFor="fullName" className="block text-[13px] font-semibold text-foreground mb-1.5">
                  Full name
                </label>
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  placeholder="Jane Trader"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3 py-2.5 text-[13px] bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="trader@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 text-[13px] bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-[13px] font-semibold text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder={mode === 'login' ? 'Your password' : 'Min. 6 characters'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-3 pr-10 py-2.5 text-[13px] bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-primary text-primary-foreground text-[14px] font-semibold rounded-lg hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                </>
              ) : mode === 'login' ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <span className="text-[13px] text-muted-foreground">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            </span>
            <button
              onClick={switchMode}
              className="text-[13px] text-primary font-semibold hover:underline"
            >
              {mode === 'login' ? 'Sign up free' : 'Sign in'}
            </button>
          </div>

          {mode === 'signup' && (
            <p className="text-[11px] text-muted-foreground text-center mt-3">
              By creating an account you agree to our{' '}
              <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>{' '}
              and{' '}
              <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
